import React from 'react';
import styled from '@benzinga/themetron';
import { extractImageSize, sanitizeHTML } from '@benzinga/frontend-utils';
import Image from 'next/image';
import { CoreElementWrapper, CoreImageWrapper } from '../CoreElement';
import classNames from 'classnames';

export interface CoreImageProps {
  block: any;
}

export const CoreImage: React.FC<CoreImageProps> = ({ block }) => {
  if (block.image) {
    const image = extractImageSize(block.image, block.attrs?.sizeSlug);
    if (!image && block.innerHTML) {
      return <CoreElementWrapper dangerouslySetInnerHTML={{ __html: sanitizeHTML(block.innerHTML) }} />;
    } else if (!image) {
      return <div />;
    }
    const imageUrl = image?.url;

    const width = Number(block.attrs.width ?? image.width ?? 200);
    const height = Number(
      block.attrs.height ?? (image.height && image.width ? (image.height * width) / image.width : 200),
    );
    const isFill = block.attrs?.layout === 'fill';

    const articleImage = isFill ? (
      <Image
        alt={block.image?.alt || block.image?.title}
        fill
        sizes={`(max-width: 800px) ${width}px, (min-width: 800px) ${width}px`}
        src={imageUrl}
        style={{ objectFit: 'cover' }}
      />
    ) : (
      <Image
        alt={block.image?.alt || block.image?.title}
        height={height}
        sizes={`(max-width: 800px) ${width}px, (min-width: 800px) ${width}px`}
        src={imageUrl}
        style={{ height: 'auto', maxWidth: '100%' }}
        width={width}
      />
    );

    const ImageComponent = (
      <CoreImageWrapper className="core-image" style={isFill ? { position: 'relative' } : undefined}>
        {block.href ? (
          <a href={block.href} rel="noreferrer" target="_blank">
            {articleImage}
          </a>
        ) : (
          articleImage
        )}
        {block.image?.caption && block.image.caption.trim() && (
          <div className="image-caption" dangerouslySetInnerHTML={{ __html: sanitizeHTML(block.image.caption) }} />
        )}
      </CoreImageWrapper>
    );
    return (
      <CoreElementWrapper
        className={classNames('image-wrapper', {
          [`${block.attrs.align}`]: !!block.attrs.align,
          [`${block.attrs.className}`]: !!block.attrs.className,
        })}
      >
        {ImageComponent}
      </CoreElementWrapper>
    );
  } else if (block?.tagAttributes) {
    const imageUrl = block?.tagAttributes?.src;
    const width = block?.tagAttributes?.width ?? 800;
    const height = block?.tagAttributes?.height ?? 450;

    const ImageComponent = (
      <Image
        alt={block.image?.alt || block.image?.title || ''}
        height={Number(height)}
        sizes={`(max-width: 800px) ${width}px, (min-width: 800px) ${width}px`}
        src={imageUrl}
        style={{ height: 'auto', maxWidth: '100%' }}
        width={Number(width)}
      />
    );

    return (
      <CoreElementWrapper className="core-image flex items-center justify-center">
        {block.href ? (
          <a href={block.href} rel="noreferrer" target="_blank">
            {ImageComponent}
          </a>
        ) : (
          ImageComponent
        )}
      </CoreElementWrapper>
    );
  } else {
    return <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(block.innerHTML) }} style={{ display: 'contents' }} />;
  }
};

export const Container = styled.div`
  &.wp-block-image {
    padding: 0.5rem;
    flex-grow: 1;
    min-width: 0;
    word-break: break-word;
    overflow-wrap: break-word;
    h2 {
      margin-top: 0;
    }
    @media (min-width: 782px) {
      flex-basis: 0;
      flex-grow: 1;
    }
    @media (max-width: 600px) {
      flex-basis: 100% !important;
    }
  }
`;
